import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GetResponsePayload,
  PARTNER_BOOKINGS_AVAILABILITY_API,
} from 'constants/payloads/partnerBookingsAvailability';
import { useApiGateway } from 'hooks/useApi';
import { buildAvailability, getTodayLocal } from './buildAvailability';

/** Until a club answers with its own zone, the strip starts on the day in our launch market. */
export const DEFAULT_TIMEZONE = 'America/New_York';

export const getAvailabilityEndpoint = (venueId: string, localDate: string) =>
  `${PARTNER_BOOKINGS_AVAILABILITY_API}?venueId=${venueId}&date=${localDate}`;

interface Params {
  venueId: string;
  venueName: string;
  timezone?: string | null;
}

/** The club's real free time, one venue-local date at a time; days are kept once loaded. */
export const useVenueAvailability = ({ venueId, venueName, timezone }: Params) => {
  const { get, isLoading } = useApiGateway<undefined, GetResponsePayload>();
  const [days, setDays] = useState<Record<string, GetResponsePayload>>({});
  const [failedDates, setFailedDates] = useState<string[]>([]);
  // Every date asked for, in flight or done, so a re-render never asks twice.
  const requestedDates = useRef(new Set<string>());
  const getRef = useRef(get);
  getRef.current = get;

  const zone = Object.values(days)[0]?.timezone ?? timezone ?? DEFAULT_TIMEZONE;
  const todayLocal = getTodayLocal(zone);

  const loadDate = useCallback(
    async (localDate: string) => {
      if (!localDate || requestedDates.current.has(localDate)) {
        return;
      }
      requestedDates.current.add(localDate);
      const response = await getRef.current({
        endpoint: getAvailabilityEndpoint(venueId, localDate),
      });
      if (response.isError || !response.data || typeof response.data === 'string') {
        setFailedDates((previous) => [...previous, localDate]);
        return;
      }
      const day = response.data;
      setDays((previous) => ({ ...previous, [localDate]: day }));
    },
    [venueId],
  );

  useEffect(() => {
    loadDate(todayLocal);
  }, [loadDate, todayLocal]);

  const availability = useMemo(
    () => buildAvailability({ venueId, venueName, timezone: zone, days }),
    [venueId, venueName, zone, days],
  );
  const firstDay = days[todayLocal] ?? Object.values(days)[0];

  return {
    availability,
    days,
    loadDate,
    isLoading,
    isBookable: firstDay?.isBookable ?? false,
    notBookableReason: firstDay?.notBookableReason ?? null,
    hasFailed: failedDates.length > 0 && Object.keys(days).length === 0,
  };
};
